import React, { useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Input } from 'reactstrap';

import apiReports, { ISalesProImport, ISalesProOffer } from 'api/reports';
import IUserGet from 'api/interfaces/users/IUserGet';
import PageContainer from 'components/layout/PageContainer';
import formatGroupedNumber from 'services/numbers/formatGroupedNumber';
import { IMPORT_SALESPRO } from 'services/enums/users/permissions';
import translations from 'services/translations/translations';
import hasPermissionType from 'services/users/hasPermissionType';

import MultiSelectFilter, { IFilterOption } from './MultiSelectFilter';
import useReportPeriod from './ReportPeriodPicker';

import 'style/reports/OfferDashboard.css';

// The rows shown before "Show all" in the table
const ROWS_SHOWN = 100;

// An Excel file is known by its name: the browsers do not always tell its type
const isExcel = (file: File) => /\.xlsx$/i.test(file.name);

// The statuses of SalesPro: open (no status), won and lost
const STATUS_KEYS = ['open', 'won', 'lost'];
const STATUS_COLORS: { [status: string]: string } = {
  open: '#2f80ed',
  won: '#27ae60',
  lost: '#eb5757',
};

const statusOf = (offer: ISalesProOffer): string => {
  const status = (offer.status ?? '').trim().toLocaleLowerCase('fi');
  if (status === 'voitettu') {
    return 'won';
  }
  return status === 'hävitty' ? 'lost' : 'open';
};

// The margin % marks of the calculation window: under 20 % red, over 30 % green
const getMarginColor = (ratio: null | number): string => {
  if (ratio === null) {
    return '#b8c0c8';
  }

  if (ratio < 20) {
    return '#eb5757';
  }

  return ratio > 30 ? '#27ae60' : '#e2b93b';
};

// The expense as given, or from the margin or the margin % of the total; null when unknown
const knownExpense = (offer: ISalesProOffer): null | number => {
  if (offer.expense !== null) {
    return offer.expense;
  }
  if (offer.total === null) {
    return null;
  }
  if (offer.margin !== null) {
    return offer.total - offer.margin;
  }
  return offer.marginRatio !== null
    ? offer.total * (1 - offer.marginRatio)
    : null;
};

interface ISums {
  count: number;
  // The margin and the sales of the offers with a known expense, the base of the margin %
  margin: number;
  marginBase: number;
  value: number;
}

const sumOf = (offers: ISalesProOffer[]): ISums =>
  offers.reduce(
    (sums, offer) => {
      const expense = knownExpense(offer);
      const value = offer.total ?? 0;
      return {
        count: sums.count + 1,
        margin: sums.margin + (expense === null ? 0 : value - expense),
        marginBase: sums.marginBase + (expense === null ? 0 : value),
        value: sums.value + value,
      };
    },
    { count: 0, margin: 0, marginBase: 0, value: 0 }
  );

const ratioOf = (sums: ISums): null | number =>
  sums.marginBase > 0 ? (sums.margin / sums.marginBase) * 100 : null;

type SortKey =
  | 'code'
  | 'customerName'
  | 'departmentName'
  | 'expense'
  | 'margin'
  | 'marginRatio'
  | 'name'
  | 'owner'
  | 'sentAt'
  | 'status'
  | 'total';

// The sorts of numbers and days start from the largest and latest
const DESCENDING_FIRST: SortKey[] = [
  'expense',
  'margin',
  'marginRatio',
  'sentAt',
  'total',
];

/**
 * The offers of SalesPro, the earlier offer program: its Excel export dropped on the page
 * (or chosen) replaces the offers imported before. Filtered as the sales report (the period
 * by the deadline, department, customer, owner and status, many of each with Ctrl) with the
 * totals of each status above. The sales report can show the open ones with those of ProBid.
 */
const SalesProOffers = ({ tabs }: { tabs?: JSX.Element }): JSX.Element => {
  const { t } = useTranslation([translations.offers]);
  const tr = (key: string, options?: Record<string, unknown>) =>
    t(`${translations.offers}:dashboard.salesPro.${key}`, options);
  const trSales = (key: string, options?: Record<string, unknown>) =>
    t(`${translations.offers}:dashboard.sales.${key}`, options);

  const { period, picker } = useReportPeriod('salespro');
  const [offers, setOffers] = useState<ISalesProOffer[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [importing, setImporting] = useState(false);
  const [imported, setImported] = useState<ISalesProImport | null>(null);
  const [importFailed, setImportFailed] = useState<null | string>(null);
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(false);
  // The filters, the values chosen in each; none chosen is all
  const [statuses, setStatuses] = useState<string[]>([]);
  const [departments, setDepartments] = useState<string[]>([]);
  const [customers, setCustomers] = useState<string[]>([]);
  const [owners, setOwners] = useState<string[]>([]);
  const [sortKey, setSortKey] = useState<SortKey>('sentAt');
  const [sortAscending, setSortAscending] = useState(false);

  const load = () => {
    setFailed(false);
    apiReports
      .getSalesPro()
      .then(setOffers)
      .catch(() => setFailed(true));
  };

  useEffect(load, []);

  const onDrop = async (files: File[]) => {
    if (files.length === 0 || !isExcel(files[0])) {
      setImportFailed(tr('Not Excel'));
      return;
    }

    setImporting(true);
    setImportFailed(null);
    setImported(null);
    try {
      setImported(await apiReports.importSalesPro(files[0]));
      load();
    } catch (error) {
      setImportFailed(tr('Import failed'));
    } finally {
      setImporting(false);
    }
  };

  // Importing needs its own permission
  const currentUser = useSelector(
    (state: { users: { current: IUserGet | null } }) => state.users.current
  );
  const mayImport = hasPermissionType(currentUser, IMPORT_SALESPRO);

  const { getInputProps, getRootProps, isDragActive } = useDropzone({
    disabled: importing || !mayImport,
    multiple: false,
    onDrop,
  });

  const day = (value: null | string) =>
    value ? new Date(value).toLocaleDateString('fi-FI') : '';
  const time = (value: string) =>
    new Date(value).toLocaleString('fi-FI', {
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      month: 'numeric',
      year: 'numeric',
    });
  const money = (value: null | number) =>
    value === null ? '–' : `${formatGroupedNumber(value, 0, 0)} €`;
  const percent = (ratio: null | number) =>
    ratio === null ? '–' : `${formatGroupedNumber(ratio, 1, 1)} %`;

  const noDepartment = tr('No department');
  const departmentOf = (offer: ISalesProOffer) =>
    offer.departmentName ?? noDepartment;
  const customerOf = (offer: ISalesProOffer) => offer.customerName ?? '';
  const ownerOf = (offer: ISalesProOffer) => offer.owner ?? '';
  const isChosen = (chosen: string[], value: string) =>
    chosen.length === 0 || chosen.includes(value);

  // The offers of the period by their deadline
  const ofPeriod = (offers ?? []).filter((offer) => {
    if (!offer.sentAt) {
      return false;
    }
    const sent = new Date(offer.sentAt);
    return sent >= period.from && sent < period.to;
  });

  // The choices of each filter from the offers of the period
  const distinct = (values: string[]) =>
    Array.from(new Set(values.filter((value) => value !== ''))).sort((a, b) =>
      a.localeCompare(b, 'fi')
    );
  const departmentOptions = distinct(ofPeriod.map(departmentOf));
  const customerOptions = distinct(ofPeriod.map(customerOf));
  const ownerOptions = distinct(ofPeriod.map(ownerOf));

  // The offers of the other filters; the status is left out so its totals stay visible
  const needle = search.trim().toLocaleLowerCase('fi');
  const withoutStatus = ofPeriod.filter(
    (offer) =>
      isChosen(departments, departmentOf(offer)) &&
      isChosen(customers, customerOf(offer)) &&
      isChosen(owners, ownerOf(offer)) &&
      (needle === '' ||
        [offer.code, offer.name, offer.customerName, offer.owner].some(
          (value) => (value ?? '').toLocaleLowerCase('fi').includes(needle)
        ))
  );
  const filtered = withoutStatus.filter((offer) =>
    isChosen(statuses, statusOf(offer))
  );

  const sortValue = (offer: ISalesProOffer): number | string => {
    const expense = knownExpense(offer);
    switch (sortKey) {
      case 'total':
        return offer.total ?? -Infinity;
      case 'expense':
        return expense ?? -Infinity;
      case 'margin':
        return expense === null ? -Infinity : (offer.total ?? 0) - expense;
      case 'marginRatio':
        return expense === null || !offer.total
          ? -Infinity
          : (offer.total - expense) / offer.total;
      case 'sentAt':
        return offer.sentAt ?? '';
      case 'departmentName':
        return departmentOf(offer).toLowerCase();
      case 'status':
        return STATUS_KEYS.indexOf(statusOf(offer));
      default:
        return `${offer[sortKey] ?? ''}`.toLowerCase();
    }
  };
  const sorted = [...filtered].sort((a, b) => {
    const first = sortValue(a);
    const second = sortValue(b);
    let order = 0;
    if (first < second) {
      order = -1;
    } else if (first > second) {
      order = 1;
    }
    return sortAscending ? order : -order;
  });
  const shown = showAll ? sorted : sorted.slice(0, ROWS_SHOWN);

  const sortBy = (key: SortKey) => {
    if (key === sortKey) {
      setSortAscending(!sortAscending);
    } else {
      setSortKey(key);
      setSortAscending(!DESCENDING_FIRST.includes(key));
    }
  };
  const sortArrow = sortAscending ? ' ▲' : ' ▼';
  const header = (key: SortKey, label: string, numeric = false) => (
    <th
      className={`${numeric ? 'numeric ' : ''}${
        sortKey === key ? 'sorted' : ''
      }`}
      onClick={() => sortBy(key)}
    >
      {label}
      {sortKey === key ? sortArrow : ''}
    </th>
  );

  const totals = sumOf(filtered);

  // The totals of each status. A click chooses the status alone (or all again), a click
  // with Ctrl (⌘ on a Mac) adds it to the chosen ones or takes it away.
  const statusTiles = STATUS_KEYS.map((key) => {
    const sums = sumOf(
      withoutStatus.filter((offer) => statusOf(offer) === key)
    );
    return { key, ratio: ratioOf(sums), sums };
  });
  const chooseStatus = (key: string, addToChosen: boolean) => {
    if (addToChosen) {
      setStatuses(
        statuses.includes(key)
          ? statuses.filter((chosen) => chosen !== key)
          : [...statuses, key]
      );
      return;
    }

    setStatuses(statuses.length === 1 && statuses[0] === key ? [] : [key]);
  };

  const marginBadge = (ratio: null | number) =>
    ratio === null ? (
      '–'
    ) : (
      <span
        className="offer-dashboard-margin"
        style={{ borderColor: getMarginColor(ratio) }}
      >
        {percent(ratio)}
      </span>
    );

  const filter = (
    label: string,
    allLabel: string,
    value: string[],
    onChange: (value: string[]) => void,
    options: IFilterOption[]
  ) => (
    <MultiSelectFilter
      allLabel={allLabel}
      chosenLabel={(count) => trSales('Chosen', { count })}
      label={label}
      onChange={onChange}
      options={options}
      value={value}
    />
  );
  const asOptions = (values: string[]): IFilterOption[] =>
    values.map((value) => ({ label: value, value }));

  const latest = offers && offers.length > 0 ? offers[0] : null;
  const importedText = latest
    ? tr('Imported at', {
        by: latest.importedBy ?? '–',
        time: time(latest.importedAt),
      })
    : tr('Nothing imported');
  const withoutDepartment = (offers ?? []).filter(
    (offer) => offer.departmentName === null
  ).length;
  const withoutDay = (offers ?? []).filter((offer) => !offer.sentAt).length;

  return (
    <PageContainer fullWidth>
      <div className="offer-dashboard">
        {tabs}
        <div className="offer-dashboard-header">
          <div>
            <h1>{tr('Title')}</h1>
            <p className="offer-dashboard-subtitle">{tr('Subtitle')}</p>
          </div>
          <div className="offer-dashboard-filters offer-sales-filters">
            {picker}
            <div className="offer-sales-filter-row">
              {filter(
                tr('Department'),
                trSales('All departments'),
                departments,
                setDepartments,
                asOptions(departmentOptions)
              )}
              {filter(
                tr('Customer'),
                trSales('All customers'),
                customers,
                setCustomers,
                asOptions(customerOptions)
              )}
              {filter(
                tr('Owner'),
                trSales('All signers'),
                owners,
                setOwners,
                asOptions(ownerOptions)
              )}
              {/* The statuses, the same choice as the boxes below */}
              {filter(
                tr('Status'),
                trSales('All statuses'),
                statuses,
                setStatuses,
                STATUS_KEYS.map((key) => ({
                  label: tr(`statuses.${key}`),
                  value: key,
                }))
              )}
              <Input
                aria-label={tr('Search')}
                className="salespro-search"
                onChange={(event) => setSearch(event.target.value)}
                placeholder={tr('Search')}
                value={search}
              />
            </div>
          </div>
        </div>

        {/* The export of SalesPro dropped here, or chosen with a click, by the users with
            the permission of the import; the others see when it was imported */}
        {mayImport ? (
          <div
            {...getRootProps({
              className: `salespro-drop${isDragActive ? ' active' : ''}${
                importing ? ' importing' : ''
              }`,
            })}
          >
            <input {...getInputProps()} />
            <strong>{importing ? tr('Importing') : tr('Drop')}</strong>
            <span>
              {tr('Drop info')} · {importedText}
            </span>
          </div>
        ) : (
          <p className="offer-dashboard-info">
            {importedText} {tr('No import permission')}
          </p>
        )}

        {imported ? (
          <p className="salespro-message">
            {tr('Imported', { count: imported.count })}
            {imported.ownersNotFound.length > 0
              ? ` ${tr('Owners not found', {
                  owners: imported.ownersNotFound.join(', '),
                })}`
              : ''}
          </p>
        ) : null}
        {importFailed ? (
          <p className="salespro-message failed">{importFailed}</p>
        ) : null}
        {failed ? (
          <p className="offer-dashboard-info">{tr('Load failed')}</p>
        ) : null}
        {offers === null && !failed ? (
          <p className="offer-dashboard-info">{tr('Loading')}</p>
        ) : null}

        {offers !== null ? (
          <>
            {/* The totals of each status; a click shows only its offers, Ctrl adds more */}
            <div className="offer-dashboard-kpis">
              {statusTiles.map((tile) => (
                <button
                  aria-pressed={statuses.includes(tile.key)}
                  className={`offer-dashboard-kpi offer-sales-phase${
                    statuses.includes(tile.key) ? ' active' : ''
                  }`}
                  key={tile.key}
                  onClick={(event) =>
                    chooseStatus(tile.key, event.ctrlKey || event.metaKey)
                  }
                  style={{ borderTopColor: STATUS_COLORS[tile.key] }}
                  title={trSales('Phase help')}
                  type="button"
                >
                  <div className="offer-dashboard-kpi-label">
                    {tr(`statuses.${tile.key}`)}
                  </div>
                  <div
                    className="offer-dashboard-kpi-value"
                    style={{ color: STATUS_COLORS[tile.key] }}
                  >
                    {money(tile.sums.value)}
                  </div>
                  <div className="offer-dashboard-kpi-note">
                    {trSales('Phase note', {
                      count: tile.sums.count,
                      margin: money(tile.sums.margin),
                      ratio: percent(tile.ratio),
                    })}
                  </div>
                  <div className="offer-dashboard-kpi-note">
                    {tr('By deadline')}
                  </div>
                </button>
              ))}
            </div>

            <section className="offer-dashboard-card offer-dashboard-table-card">
              <h2>{tr('Offers', { count: filtered.length })}</h2>
              <p className="offer-dashboard-card-info">
                {tr('Table info')}
                {withoutDepartment > 0
                  ? ` ${tr('Without department', { count: withoutDepartment })}`
                  : ''}
                {withoutDay > 0
                  ? ` ${tr('Without day', { count: withoutDay })}`
                  : ''}
              </p>
              <div className="offer-dashboard-table-wrapper">
                <table className="offer-dashboard-table">
                  <thead>
                    <tr>
                      {header('sentAt', tr('Sent at'), true)}
                      {header('code', tr('Code'))}
                      {header('name', tr('Name'))}
                      {header('customerName', tr('Customer'))}
                      {header('departmentName', tr('Department'))}
                      {header('owner', tr('Owner'))}
                      {header('status', tr('Status'))}
                      {header('total', tr('Total'), true)}
                      {header('expense', tr('Expense'), true)}
                      {header('margin', tr('Margin'), true)}
                      {header('marginRatio', tr('Margin %'), true)}
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map((offer) => {
                      const expense = knownExpense(offer);
                      const status = statusOf(offer);
                      return (
                        <tr key={offer.id}>
                          <td className="numeric">{day(offer.sentAt)}</td>
                          <td>{offer.code}</td>
                          <td className="target">{offer.name}</td>
                          <td>{customerOf(offer)}</td>
                          <td>{departmentOf(offer)}</td>
                          <td>{ownerOf(offer)}</td>
                          <td>
                            <span
                              className="offer-dashboard-status"
                              style={{ background: STATUS_COLORS[status] }}
                            >
                              {tr(`statuses.${status}`)}
                            </span>
                          </td>
                          <td className="numeric">{money(offer.total)}</td>
                          <td className="numeric">{money(expense)}</td>
                          <td className="numeric">
                            {expense === null
                              ? '–'
                              : money((offer.total ?? 0) - expense)}
                          </td>
                          <td className="numeric">
                            {marginBadge(
                              expense === null || !offer.total
                                ? null
                                : ((offer.total - expense) / offer.total) * 100
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={7}>{tr('Sum')}</td>
                      <td className="numeric">{money(totals.value)}</td>
                      <td className="numeric">
                        {money(totals.marginBase - totals.margin)}
                      </td>
                      <td className="numeric">{money(totals.margin)}</td>
                      <td className="numeric">
                        {marginBadge(ratioOf(totals))}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              {sorted.length > ROWS_SHOWN ? (
                <button
                  className="offer-dashboard-show-all"
                  onClick={() => setShowAll(!showAll)}
                  type="button"
                >
                  {showAll
                    ? t(`${translations.offers}:dashboard.Show less`)
                    : t(`${translations.offers}:dashboard.Show all`, {
                        count: sorted.length,
                      })}
                </button>
              ) : null}
            </section>
          </>
        ) : null}
      </div>
    </PageContainer>
  );
};

export default SalesProOffers;
